import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Play, Trophy, Clock, Terminal, ChevronRight } from 'lucide-react'
import { SectionGrid } from '../SectionGrid'
import { coursesAPI } from '../../../api/courses'
import { exercisesAPI } from '../../../api/exercises'
import { calcDurationMinutes, formatDuration } from '../../../utils/duration'
import type { LabModule } from '../../../types/api'

interface LabModuleWithProgress extends LabModule {
  status?: string
  completed_exercises?: number
  total_exercises?: number
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

interface RevealOnScrollProps {
  children: React.ReactNode
  className?: string
  delay?: number
}

const RevealOnScroll = ({ children, className, delay = 0 }: RevealOnScrollProps): React.JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(prefersReducedMotion)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const node = ref.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.unobserve(node)
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -60px 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  )
}

type ModuleVariant = 'completed' | 'current' | 'upcoming' | 'empty'

const GOLD = '#7a6020'
const GOLD_LIGHT = '#D4AF37'
const GOLD_TINT = '#f5ede0'
const GOLD_BORDER = '#e0d4be'

interface PathMarkerProps {
  variant: ModuleVariant
  label: string
}

const PathMarker = ({ variant, label }: PathMarkerProps): React.JSX.Element => {
  if (variant === 'completed') {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: GOLD }}>
        <Check className="w-[18px] h-[18px] text-white" />
      </div>
    )
  }
  if (variant === 'current') {
    return (
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: GOLD_LIGHT }}>
        <Play className="w-4 h-4 fill-current" style={{ color: '#4a3710' }} />
      </div>
    )
  }
  if (variant === 'empty') {
    return (
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: '#fafafa', border: '1px dashed #d1d5db' }}
      >
        <Clock className="w-4 h-4" style={{ color: '#9ca3af' }} />
      </div>
    )
  }
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
      style={{ background: '#ffffff', border: `1.5px solid ${GOLD_BORDER}` }}
    >
      <span className="text-xs font-bold" style={{ color: GOLD }}>{label}</span>
    </div>
  )
}

interface PathCardProps {
  module: LabModuleWithProgress
  moduleLabel: string
  exerciseCount: number
  variant: ModuleVariant
  onClick: () => void
}

const PathCard = ({ module, moduleLabel, exerciseCount, variant, onClick }: PathCardProps): React.JSX.Element => {
  const progress = module.progress ?? 0
  const isEmpty = variant === 'empty'

  const cardStyle: React.CSSProperties =
    variant === 'current'
      ? { background: '#ffffff', border: `1.5px solid ${GOLD_LIGHT}` }
      : isEmpty
        ? { background: '#fafafa', border: `1px dashed ${GOLD_BORDER}` }
        : { background: '#ffffff', border: `1px solid ${GOLD_BORDER}` }

  const badge =
    variant === 'completed'
      ? { text: 'Completed', bg: GOLD_TINT, color: GOLD }
      : variant === 'current'
        ? { text: progress > 0 ? 'In progress' : 'Up next', bg: GOLD_TINT, color: GOLD }
        : isEmpty
          ? { text: 'Coming soon', bg: '#f3f4f6', color: '#9ca3af' }
          : null

  return (
    <div className="w-full max-w-md rounded-xl p-4 flex flex-col gap-2" style={cardStyle}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold" style={{ color: isEmpty ? '#9ca3af' : GOLD }}>
          {moduleLabel}
        </span>
        {badge && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
            style={{ background: badge.bg, color: badge.color }}
          >
            {badge.text}
          </span>
        )}
      </div>

      <h3 className="text-sm font-bold leading-snug" style={{ color: isEmpty ? '#9ca3af' : '#111827' }}>
        {module.title}
      </h3>

      <p className="text-xs leading-relaxed line-clamp-2" style={{ color: isEmpty ? '#b0b0b0' : '#6b7280' }}>
        {module.description || 'Explore comprehensive topics and hands-on exercises in this module.'}
      </p>

      <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#9ca3af' }}>
        <Clock className="w-3 h-3" />
        <span>{isEmpty ? '—' : formatDuration(calcDurationMinutes(exerciseCount))}</span>
        <span className="mx-0.5">•</span>
        <Terminal className="w-3 h-3" />
        <span>{isEmpty ? 'No labs yet' : `${exerciseCount} Labs`}</span>
      </div>

      {variant === 'current' && progress > 0 && (
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
          <div className="h-full rounded-full" style={{ width: `${progress}%`, background: GOLD }} />
        </div>
      )}

      {isEmpty ? (
        <div className="mt-1 w-full text-center py-2 text-xs font-medium" style={{ color: '#9ca3af' }}>
          No labs in this module yet
        </div>
      ) : variant === 'current' ? (
        <button
          onClick={onClick}
          className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold text-white transition-colors hover:opacity-90"
          style={{ background: GOLD }}
        >
          {progress > 0 ? 'Continue Learning' : 'Start Module'} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      ) : variant === 'completed' ? (
        <button
          onClick={onClick}
          className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: GOLD_BORDER, color: '#374151' }}
        >
          Review Lab <ChevronRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          onClick={onClick}
          className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: GOLD_BORDER, color: '#374151' }}
        >
          Start Module <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

interface ExamNodeProps {
  allCompleted: boolean
}

const ExamNode = ({ allCompleted }: ExamNodeProps): React.JSX.Element => (
  <div className="w-full max-w-md rounded-xl p-4" style={{ background: GOLD_TINT, border: `1px dashed ${GOLD_LIGHT}` }}>
    <p className="text-sm font-bold" style={{ color: GOLD }}>Certification Exam</p>
    <p className="text-xs mt-1 leading-relaxed" style={{ color: '#8a7550' }}>
      {allCompleted
        ? 'All modules are complete — unlock the final practice exam and earn your certification badge.'
        : 'Complete all modules to unlock the final ONTAP Administration practice exam and earn your certification badge.'}
    </p>
    <button
      disabled={!allCompleted}
      className="mt-3 w-full px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
      style={{
        background: allCompleted ? GOLD : '#E5E7EB',
        color: allCompleted ? '#fff' : '#9CA3AF',
        cursor: allCompleted ? 'pointer' : 'default'
      }}
    >
      Unlock Certification Exam
    </button>
  </div>
)

interface AvailableModulesProps {
  courseId: string | undefined
  onModulesLoaded?: (modules: LabModuleWithProgress[]) => void
  onTotalDurationMinutes?: (minutes: number) => void
}

type StatusTab = 'all' | 'in_progress' | 'completed' | 'not_started'

const TABS: { key: StatusTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'not_started', label: 'Yet to Learn' }
]

const categorize = (module: LabModuleWithProgress): Exclude<StatusTab, 'all'> => {
  const progress = module.progress ?? 0
  if (progress >= 100) return 'completed'
  if (progress > 0) return 'in_progress'
  return 'not_started'
}

export const AvailableModules: React.FC<AvailableModulesProps> = ({
  courseId,
  onModulesLoaded,
  onTotalDurationMinutes
}) => {
  const navigate = useNavigate()
  const [modules, setModules] = useState<LabModuleWithProgress[]>([])
  const [exerciseCounts, setExerciseCounts] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [pillStyle, setPillStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 })
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const tabsContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const measure = (): void => {
      const btn = tabRefs.current.get(activeTab)
      if (btn) setPillStyle({ left: btn.offsetLeft, width: btn.offsetWidth })
    }
    measure()

    const container = tabsContainerRef.current
    if (!container) return
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [activeTab, loading])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!courseId) {
        setLoading(false)
        return
      }
      try {
        const data = await coursesAPI.getCourseModules(courseId)
        setModules(data)
        onModulesLoaded?.(data)

        const counts = await Promise.all(
          data.map((m: LabModuleWithProgress) =>
            exercisesAPI
              .getModuleExercises(String(m.id))
              .then((ex: unknown[]) => ({ id: m.id, count: ex.length }))
              .catch(() => ({ id: m.id, count: 0 }))
          )
        )
        const countMap: Record<number, number> = {}
        let total = 0
        counts.forEach(({ id, count }) => {
          countMap[id] = count
          total += count
        })
        setExerciseCounts(countMap)
        onTotalDurationMinutes?.(calcDurationMinutes(total))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [courseId])

  const counts: Record<StatusTab, number> = { all: modules.length, in_progress: 0, completed: 0, not_started: 0 }
  modules.forEach((m) => { counts[categorize(m)]++ })

  const visible = modules
    .map((module, idx) => ({ module, idx }))
    .filter(({ module }) => activeTab === 'all' || categorize(module) === activeTab)

  const activeTabLabel = TABS.find((t) => t.key === activeTab)?.label ?? ''

  const nextModuleId = modules.find((m) => (m.progress ?? 0) < 100 && (exerciseCounts[m.id] ?? 0) > 0)?.id
  const totalModules = modules.length
  const completedModules = modules.filter((m) => (m.progress ?? 0) >= 100).length
  const allCompleted = totalModules > 0 && completedModules >= totalModules

  const getVariant = (module: LabModuleWithProgress, exerciseCount: number): ModuleVariant => {
    if (exerciseCount === 0) return 'empty'
    if ((module.progress ?? 0) >= 100) return 'completed'
    if (module.id === nextModuleId) return 'current'
    return 'upcoming'
  }

  interface PathItem {
    key: string
    side: 'left' | 'right'
    marker: React.JSX.Element
    card: React.JSX.Element
  }

  const moduleEntries = visible.map(({ module, idx }) => {
    const exerciseCount = exerciseCounts[module.id] ?? 0
    const variant = getVariant(module, exerciseCount)
    const moduleLabel = `M${module.order_index != null ? module.order_index - 1 : idx}`
    return { module, idx, exerciseCount, variant, moduleLabel }
  })

  const pathItems: PathItem[] = moduleEntries.map(({ module, idx, exerciseCount, variant, moduleLabel }) => ({
    key: String(module.id),
    side: idx % 2 === 0 ? 'left' : 'right',
    marker: <PathMarker variant={variant} label={moduleLabel} />,
    card: (
      <PathCard
        module={module}
        moduleLabel={moduleLabel}
        exerciseCount={exerciseCount}
        variant={variant}
        onClick={() => navigate(`/course/${courseId}/module/${module.id}/exercises`)}
      />
    )
  }))

  if (activeTab === 'all' && modules.length > 0) {
    pathItems.push({
      key: 'certification-exam',
      side: modules.length % 2 === 0 ? 'left' : 'right',
      marker: (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: GOLD_TINT, border: `1.5px dashed ${GOLD_LIGHT}` }}
        >
          <Trophy className="w-4 h-4" style={{ color: GOLD }} />
        </div>
      ),
      card: <ExamNode allCompleted={allCompleted} />
    })
  }

  return (
    <SectionGrid
      title="Modules"
      subtitle="Follow the structured path to complete your certification."
      loading={loading}
      error={error}
      emptyText="This course doesn't have any lab modules yet."
      loadingText="Loading modules..."
      gridClassName=""
      filterBar={
        modules.length > 0 ? (
          <div
            ref={tabsContainerRef}
            className="relative flex w-full items-center rounded-full border border-[#7a6020]/15 bg-white p-1 shadow-sm sm:inline-flex sm:w-max"
          >
            <span
              className="absolute top-1 bottom-1 rounded-full bg-[#7a6020] shadow-sm transition-all duration-300 ease-in-out"
              style={{ left: pillStyle.left, width: pillStyle.width }}
            />
            {TABS.map((tab) => {
              const active = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  ref={(el) => { if (el) tabRefs.current.set(tab.key, el) }}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative z-10 flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-colors duration-300 sm:flex-none sm:px-3 sm:text-xs ${
                    active ? 'text-white' : 'text-[#7a6020]/70 hover:text-[#1a1810]'
                  }`}
                >
                  <span className="truncate">{tab.label}</span>
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none transition-colors duration-300 ${
                      active ? 'bg-white/20 text-white' : 'bg-[#f5ede0] text-[#7a6020]/70'
                    }`}
                  >
                    {counts[tab.key]}
                  </span>
                </button>
              )
            })}
          </div>
        ) : undefined
      }
    >
      {modules.length === 0 ? null : visible.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-center">
          <p className="text-sm text-gray-500">No {activeTabLabel.toLowerCase()} modules.</p>
        </div>
      ) : activeTab !== 'all' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {moduleEntries.map(({ module, exerciseCount, variant, moduleLabel }, i) => (
            <RevealOnScroll key={module.id} delay={Math.min(i * 70, 350)}>
              <PathCard
                module={module}
                moduleLabel={moduleLabel}
                exerciseCount={exerciseCount}
                variant={variant}
                onClick={() => navigate(`/course/${courseId}/module/${module.id}/exercises`)}
              />
            </RevealOnScroll>
          ))}
        </div>
      ) : (
        <>
          <div className="hidden lg:block relative">
            <div
              className="absolute left-1/2 -translate-x-1/2 top-5 bottom-5 w-0.5"
              style={{ background: GOLD_BORDER }}
            />
            <div className="flex flex-col gap-4">
              {pathItems.map((item, i) => (
                <RevealOnScroll key={item.key} className="relative flex items-start" delay={Math.min(i * 70, 350)}>
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 z-10">{item.marker}</div>
                  {item.side === 'left' ? (
                    <>
                      <div className="w-1/2 pr-12 flex justify-end">{item.card}</div>
                      <div className="w-1/2" />
                    </>
                  ) : (
                    <>
                      <div className="w-1/2" />
                      <div className="w-1/2 pl-12 flex justify-start">{item.card}</div>
                    </>
                  )}
                </RevealOnScroll>
              ))}
            </div>
          </div>

          <div className="lg:hidden relative pl-14">
            <div
              className="absolute top-5 bottom-5 w-0.5"
              style={{ left: '19px', background: GOLD_BORDER }}
            />
            <div className="flex flex-col gap-4">
              {pathItems.map((item, i) => (
                <RevealOnScroll key={item.key} className="relative" delay={Math.min(i * 70, 350)}>
                  <div className="absolute top-0" style={{ left: '-56px' }}>{item.marker}</div>
                  {item.card}
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </>
      )}
    </SectionGrid>
  )
}
